import type {
  FormulatorAreaTouch,
  FormulatorAwaiting,
  FormulatorDraft,
  FormulatorDraftSource,
  FormulatorDuplicate,
  FormulatorFinding,
  FormulatorSlotGuide,
} from "@ttc-spectree/toolbox-contract";
import { FORMULATOR_PATH } from "@/lib/toolbox/tools";
import { api, ApiError } from "./client";
import type {
  WorkItemReadiness,
  WorkItemRef,
  WorkItemSectionKey,
} from "./work-items";

/**
 * Client for the Work-Item Formulator interview API
 * (`/api/v1/work-items/interview`, epic PI-SPEC-SW-E276, features
 * PI-SPEC-SW-1363 (API) and PI-SPEC-SW-1364 (web)).
 *
 * Two modes (D3): `create` interviews for a complete new bug or story;
 * `improve` pre-seeds from an existing item and asks only about what it is
 * missing. Nothing is written to the tracker until `submit` is called with the
 * `diffHash` of the review the user approved (D7).
 *
 * Since epic PI-SPEC-SW-E282 the interview turns run on the person's own
 * Claude Code seat inside SpecTree Toolbox: the Toolbox Formulator page
 * (`/toolbox/formulator`, feature PI-SPEC-SW-1418) starts sessions and sends
 * replies through `window.spectreeToolbox.formulator`, never through this
 * client. The page reads the session, the resume list and the review, and
 * submits, through the API calls below.
 */

export type InterviewMode = "create" | "improve";
/** The Formulator handles bugs and user stories only. */
export type InterviewKind = "bug" | "story";
export type InterviewStatus = "active" | "submitting" | "complete";
/** Whose move it is: `claude` (the engine owes the next turn) or `person`. */
export type InterviewAwaiting = FormulatorAwaiting;

/** Whether the session has a stored draft matching its answers (epic PI-SPEC-SW-E321). */
export type InterviewDraftStatus = "none" | "current";

/** The draft fields the interview view and the review share (epic PI-SPEC-SW-E321). */
export interface InterviewDraftInfo {
  /** The basis a draft must carry; present only once the interview is complete, null before. */
  draftBasis: string | null;
  /** `current` when a draft matching the answers is stored, else `none`. */
  draftStatus: InterviewDraftStatus;
  /** Who wrote the current draft; null when there is none. */
  draftSource: FormulatorDraftSource | null;
  /** The current draft; null when there is none. */
  draft: FormulatorDraft | null;
}

/** One message in the interview transcript. */
export interface InterviewTurn {
  role: "assistant" | "user";
  content: string;
  /** Assistant messages: the quick picks offered with that question. */
  quickPicks?: string[];
}

/** One target slot and whether it is satisfied yet. */
export interface InterviewSlotStatus {
  id: string;
  label: string;
  filled: boolean;
}

/** The interview as it stands after every call. */
export interface InterviewView extends Partial<InterviewDraftInfo> {
  sessionId: string;
  tracker: "ado";
  mode: InterviewMode;
  kind: InterviewKind;
  container: string;
  /** Improve mode: the item being improved. */
  itemId: string | null;
  /** Improve mode: the revision the write will be guarded with. */
  targetRevision: number | null;
  status: InterviewStatus;
  /** Whose move it is: `claude` while the engine owes the next turn (for example the opening turn). */
  awaiting: InterviewAwaiting;
  /** The question awaiting an answer, or null (opening turn pending, or every target slot is satisfied). */
  question: string | null;
  complete: boolean;
  slots: InterviewSlotStatus[];
  /**
   * Target slots then optional slots, with their parts and current values (the live Draft).
   * A part's `confirmed: false` (contract formulator 4, epic PI-SPEC-SW-E322) marks a value
   * inferred from the opening text, the title or the item's existing text that the person
   * has not confirmed yet; its slot is not `filled` until they do.
   */
  slotGuide: FormulatorSlotGuide[];
  /**
   * Contract formulator 4 (epic PI-SPEC-SW-E322): `true` when SpecTree asks the engine to
   * report inferred values so the person confirms them. Absent on an older SpecTree.
   */
  confirmInferred?: true;
  /** Create mode: what the person typed at the start; null in improve mode. */
  description: string | null;
  /** What Claude found in the code that the item should name. */
  findings: FormulatorFinding[];
  /** Product areas the change touches, as Claude reported them. */
  productAreas: FormulatorAreaTouch[];
  /** Create mode, after the opening turn: open items that may already describe the same thing (at most five). */
  duplicates: FormulatorDuplicate[];
  /** Quick picks offered with the pending question; empty when none is pending. */
  quickPicks: string[];
  /** The tracker's severity labels (bugs); empty for stories. */
  severityOptions: string[];
  messages: InterviewTurn[];
}

/** One unfinished session in the caller's resume list (`GET /work-items/interview`). */
export interface InterviewSessionSummary {
  sessionId: string;
  mode: InterviewMode;
  kind: InterviewKind;
  container: string;
  itemId: string | null;
  /** The answered title, the item's title, or the start of the description. */
  title: string;
  updatedAt: string;
  expiresAt: string;
  complete: boolean;
  awaiting: InterviewAwaiting;
}

/** Where the written item lands. */
export interface InterviewDestination {
  /** Area path: the item's own (improve) or the project's default area (create). */
  area: string;
  /** Iteration: the item's own (improve) or `Backlog` (create). */
  iteration: string;
}

/** A field the interview would change. */
export type FormulatorField = "title" | WorkItemSectionKey | "severity";

/** One changed field: the tracker's current value and the value that will be written. */
export interface FieldDiff {
  field: FormulatorField;
  label: string;
  /** `html` for sections (untrusted: sanitise before rendering), `text` for title and severity. */
  format: "html" | "text";
  before: string;
  after: string;
}

/** What the user approves before anything is written. */
export interface InterviewReview extends Partial<InterviewDraftInfo> {
  sessionId: string;
  mode: InterviewMode;
  kind: InterviewKind;
  container: string;
  itemId: string | null;
  targetRevision: number | null;
  complete: boolean;
  /** Changed fields only, in a fixed order. */
  fields: FieldDiff[];
  /** The rubric's verdict on the item as it would read after the write. */
  projectedReadiness: WorkItemReadiness;
  /** Product areas the change touches, as Claude reported them. */
  productAreas: FormulatorAreaTouch[];
  /** Where the item lands (area and iteration). */
  destination: InterviewDestination;
  /** sha256 hex of the after-values; submit must echo it. */
  diffHash: string;
}

/** The outcome of a successful submit. */
export interface InterviewSubmitResult {
  sessionId: string;
  mode: InterviewMode;
  ref: WorkItemRef;
  /** The tracker revision after the write. */
  version: number;
  /** Live readiness of the item as the tracker now stores it. */
  readiness: WorkItemReadiness;
  /** `/spectree:ado-to-er <id>`, present only when the item is ready for grooming (D13). */
  convertCommand?: string;
  /** The Formulator improve link, present only when the item is still not ready. */
  formulatorPath?: string;
}

export interface SubmitInterviewInput {
  sessionId: string;
  diffHash: string;
}

/** The person's edit of the draft (epic PI-SPEC-SW-E321); `draft.basis` is the basis the review showed. */
export interface SaveInterviewDraftInput {
  sessionId: string;
  draft: FormulatorDraft;
}

export interface DataResponse<T> {
  data: T;
}

const BASE = "/work-items/interview";

function sessionPath(sessionId: string): string {
  return `${BASE}/${encodeURIComponent(sessionId)}`;
}

export const workItemInterviewApi = {
  /** The caller's unfinished sessions: active and unexpired, newest first. */
  listActive: () => api.get<DataResponse<InterviewSessionSummary[]>>(BASE),

  /** The interview as it stands (resume after reload, refetch after a turn). */
  get: (sessionId: string) => api.get<DataResponse<InterviewView>>(sessionPath(sessionId)),

  /** The before/after diff, the projected readiness and the `diffHash` submit must echo. */
  review: (sessionId: string) =>
    api.get<DataResponse<InterviewReview>>(`${sessionPath(sessionId)}/review`),

  /**
   * Store the person's edit of the draft (epic PI-SPEC-SW-E321). Returns the
   * new review, whose `diffHash` follows the edit. Refused with 409
   * `DRAFT_STALE` when the answers changed since the basis the review showed.
   */
  saveDraft: ({ sessionId, draft }: SaveInterviewDraftInput) =>
    api.put<DataResponse<InterviewReview>>(`${sessionPath(sessionId)}/draft`, { draft }),

  /** Write the approved diff to the tracker as the caller. */
  submit: ({ sessionId, diffHash }: SubmitInterviewInput) =>
    api.post<DataResponse<InterviewSubmitResult>>(`${sessionPath(sessionId)}/submit`, { diffHash }),
};

/**
 * The specific API error code of a failed interview request.
 *
 * Interview errors carry their specific code in `error.details.code`
 * (`WORK_ITEM_CHANGED`, `DIFF_NOT_APPROVED`, `ADO_PAT_INVALID`, ...), while
 * `error.code` holds the generic class code (`CONFLICT`, `VALIDATION_ERROR`).
 * The specific code wins when present.
 */
export function getInterviewErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (!data || typeof data !== "object" || !("error" in data)) return undefined;
  const body = (data as { error?: { code?: unknown; details?: { code?: unknown } } }).error;
  const specific = body?.details?.code;
  if (typeof specific === "string") return specific;
  return typeof body?.code === "string" ? body.code : undefined;
}

/** True when submit was refused because the item changed in the tracker since the interview started. */
export function isWorkItemChangedError(error: unknown): boolean {
  return getInterviewErrorCode(error) === "WORK_ITEM_CHANGED";
}

/** The Toolbox Formulator, improving one item: `/toolbox/formulator?mode=improve&project=..&id=..`. */
export function formulatorImprovePath(project: string, id: string): string {
  const query = new URLSearchParams({ mode: "improve", project, id });
  return `${FORMULATOR_PATH}?${query.toString()}`;
}

/** The Toolbox Formulator, resuming one session: `/toolbox/formulator?session=..`. */
export function formulatorSessionPath(sessionId: string): string {
  return `${FORMULATOR_PATH}?${new URLSearchParams({ session: sessionId }).toString()}`;
}
